import { CSSProperties, HTMLAttributes } from 'react';

export type FlexProps = HTMLAttributes<HTMLDivElement> & {
  row?: boolean;
  col?: boolean;
  item?: boolean;
  reverse?: boolean;
  align?: Exclude<CSSProperties['alignItems'], 'flex-start' | 'flex-end'>;
  alignSelf?: Exclude<CSSProperties['alignItems'], 'flex-start' | 'flex-end'>;
  justify?: Exclude<CSSProperties['justifyContent'], 'flex-start' | 'flex-end'>;
  flex?:
    | CSSProperties['flex']
    | { shrink: number; grow: number; basis: number };
  gap?: number | [colGap: number, rowGap?: number];
  wrap?: boolean | CSSProperties['flexFlow'];
};

const Flex = ({
  align,
  flex,
  item,
  justify,
  gap,
  reverse = false,
  alignSelf: _alignSelf,
  wrap,
  row,
  col,
  ...props
}: FlexProps): JSX.Element => {
  const direction = row ? 'row' : col ? 'column' : 'row';
  const flexDirection = direction + (reverse ? '-reverse' : '');
  const flexFlow = {
    flexFlow: typeof wrap === 'string' ? wrap : wrap ? 'wrap' : 'nowrap',
  };
  const justifyContent =
    justify === 'start' || justify === 'end' ? 'flex-' + justify : justify;
  const [columnGap, rowGap] = Array.isArray(gap) ? gap : [gap, gap];
  const gapStyle = { columnGap, rowGap };
  const flexStyle =
    typeof flex === 'object'
      ? {
          flexBasis: flex?.basis,
          flexGrow: flex?.grow,
          flexShrink: flex?.shrink,
        }
      : { flex };
  const alignItems =
    align === 'start' || align === 'end' ? 'flex-' + align : align;
  const alignSelf =
    _alignSelf === 'start' || _alignSelf === 'end'
      ? 'flex-' + _alignSelf
      : _alignSelf;
  const itemStyle = {
    ...gapStyle,
    ...flexStyle,
    ...flexFlow,
    alignItems,
    alignSelf,
    display: !item ? 'flex' : undefined,
    justifyContent,
    flexDirection: flexDirection as CSSProperties['flexDirection'],
  };
  props.style = { ...itemStyle, ...props.style };
  return <div {...props} />;
};

export type FlexRowColProps = Omit<FlexProps, 'row' | 'col' | 'item'>;
export type FlexItemProps = Omit<
  FlexProps,
  'row' | 'col' | 'item' | 'reverse' | 'align' | 'justify' | 'gap'
>;
Flex.Row = (props: FlexRowColProps) => <Flex row {...props} />;
Flex.Col = (props: FlexRowColProps) => <Flex col {...props} />;
Flex.Item = (props: FlexItemProps) => <Flex item {...props} />;

export default Flex;
